import * as THREE from 'three';
import type { DiceCatchTuning } from './dice-catch-tuning';

export interface DiceCatchLayoutInput {
  readonly anchorPosition: THREE.Vector3;
  readonly tuning: DiceCatchTuning;
}

export interface DiceCatchLayout {
  readonly diceAnchor: THREE.Vector3;
  readonly catchDiceCenter: THREE.Vector3;
  readonly characterPosition: THREE.Vector3;
  readonly characterOffset: THREE.Vector3;
  readonly characterYaw: number;
  readonly targetHop: number;
  readonly holdScale: number;
  readonly peakScale: number;
  readonly backDirection: THREE.Vector3;
  readonly sideDirection: THREE.Vector3;
}

export const DICE_CATCH_AIRBORNE_CENTER_Y_OFFSET = 2.5;
export const DICE_CATCH_CHARACTER_YAW = 0;
const DICE_FORWARD_DIRECTION = new THREE.Vector3(0, 0, 1);
const DICE_CATCH_BACK_DIRECTION = new THREE.Vector3(0, 0, -1);
const DICE_CATCH_SIDE_DIRECTION = new THREE.Vector3(1, 0, 0);

export function computeDiceCatchLayout(input: DiceCatchLayoutInput): DiceCatchLayout {
  const { anchorPosition, tuning } = input;
  const diceAnchor = computeDiceAnchor(anchorPosition, tuning);
  const catchDiceCenter = diceAnchor
    .clone()
    .add(new THREE.Vector3(0, DICE_CATCH_AIRBORNE_CENTER_Y_OFFSET, 0));
  const backDirection = DICE_CATCH_BACK_DIRECTION.clone();
  const sideDirection = DICE_CATCH_SIDE_DIRECTION.clone();
  const horizontalOffset = backDirection
    .clone()
    .multiplyScalar(tuning.backOffset)
    .add(sideDirection.clone().multiplyScalar(tuning.sideOffset));
  const characterPosition = catchDiceCenter
    .clone()
    .add(horizontalOffset)
    .add(new THREE.Vector3(0, tuning.jumpMaxHeight - DICE_CATCH_AIRBORNE_CENTER_Y_OFFSET, 0));
  const characterOffset = characterPosition.clone().sub(anchorPosition);
  const targetHop = characterOffset.y;

  return {
    diceAnchor,
    catchDiceCenter,
    characterPosition,
    characterOffset,
    characterYaw: DICE_CATCH_CHARACTER_YAW,
    targetHop,
    holdScale: tuning.heroScaleHold,
    peakScale: tuning.heroScalePeak,
    backDirection,
    sideDirection,
  };
}

function computeDiceAnchor(anchorPosition: THREE.Vector3, tuning: DiceCatchTuning): THREE.Vector3 {
  return anchorPosition
    .clone()
    .add(DICE_FORWARD_DIRECTION.clone().multiplyScalar(tuning.diceForwardOffset))
    .add(new THREE.Vector3(0, tuning.diceCenterYOffset, 0));
}
